import { Component, Inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { combineLatest, timer } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  requestVerificationCode,
  stopVerificationTimer,
  updateUserEmail,
} from 'src/app/root-store/jobseeker-store/jobseeker-settings/actions';
import {
  selectIsOTPCodeExpired,
  selectIsOTPCodeNotValid,
  selectIsUpdateUserEmailSuccess,
  selectNextAllowedDate,
  selectRequestId,
  selectRequestVerificationCodeLoading,
  selectVerificationTimer,
} from 'src/app/root-store/jobseeker-store/jobseeker-settings/selectors';

@Component({
  selector: 'app-jobseeker-update-password-dialog',
  templateUrl: './jobseeker-update-email.component.html',
  styleUrl: './jobseeker-update-email.component.scss',
  standalone: false,
})
export class VerificationDialogComponent {
  verificationForm: FormGroup;

  readonly loading: Signal<boolean | undefined>;
  readonly verificationTimer: Signal<number | undefined>;
  readonly requestId: Signal<string | undefined>;
  readonly resendCooldownSeconds: Signal<number>;
  readonly isOTPCodeNotValid: Signal<boolean | undefined>;
  readonly isOTPCodeExpired: Signal<boolean | undefined>;
  readonly isSuccess: Signal<boolean>;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<VerificationDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { newEmail: string; password: string },
    private store: Store
  ) {
    this.verificationForm = this.fb.group({
      password: [data.password || null, Validators.required],
      newEmail: [data.newEmail || null, Validators.required],
      validationCode: [null, Validators.required]
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.store.dispatch(stopVerificationTimer());
    });

    this.loading = toSignal(this.store.select(selectRequestVerificationCodeLoading));
    this.verificationTimer = toSignal(this.store.select(selectVerificationTimer));
    this.requestId = toSignal(this.store.select(selectRequestId));
    this.isOTPCodeNotValid = toSignal(this.store.select(selectIsOTPCodeNotValid));
    this.isOTPCodeExpired = toSignal(this.store.select(selectIsOTPCodeExpired))

    const cooldown$ = combineLatest([
      this.store.select(selectNextAllowedDate),
      timer(0, 1000)
    ]).pipe(
      map(([dateStr]) => this.toRemainingSeconds(dateStr)),
      distinctUntilChanged()
    );
    this.resendCooldownSeconds = toSignal(cooldown$, { initialValue: 0 });

    this.isSuccess = toSignal(this.store.select(selectIsUpdateUserEmailSuccess), { initialValue: false });
  }

  onSubmit() {
    const requestId = this.requestId();
    if (this.verificationForm.valid && requestId) {
      const value = this.verificationForm.value;

      this.store.dispatch(updateUserEmail({
        payload: {
          requestId,
          currentPassword: value.password,
          validationCode: value.validationCode,
          newEmail: value.newEmail
        }
      }));
    }
  }

  resendVerificationCode() {
    if (this.resendCooldownSeconds() > 0) return;
    this.store.dispatch(requestVerificationCode({ newEmail: this.data.newEmail, password: this.data.password }));
  }

  public format(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
  }

  onClose() {
    this.dialogRef.close();
  }

  private toRemainingSeconds(dateStr: string | null | undefined): number {
    if (!dateStr) return 0;
    const normalized = /[Zz]|[+-]\d{2}:?\d{2}$/.test(dateStr) ? dateStr : dateStr + 'Z';
    const remainingMs = new Date(normalized).getTime() - Date.now();
    return Math.max(0, Math.ceil(remainingMs / 1000));
  }
}
